// MyLeaves.js — mes demandes de conges
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { CalendarDays, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';

const TYPES = { annual: 'Congé annuel', sick: 'Maladie', maternity: 'Maternité', paternity: 'Paternité', unpaid: 'Sans solde', other: 'Autre' };
const BADGES = {
  pending:  { cls: 'bg-yellow-100 text-yellow-700', label: 'En attente', Icon: Clock },
  approved: { cls: 'bg-green-100 text-green-700',  label: 'Approuvé',   Icon: CheckCircle },
  rejected: { cls: 'bg-red-100 text-red-700',      label: 'Refusé',     Icon: XCircle },
};

export default function MyLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ type: 'annual', startDate: '', endDate: '', reason: '' });
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  const load = () => api.get('/leaves/my').then(r => setLeaves(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setOk(''); setLoading(true);
    try {
      await api.post('/leaves', form);
      setOk('Demande envoyée — en attente de validation.');
      setForm({ type: 'annual', startDate: '', endDate: '', reason: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la demande');
    } finally { setLoading(false); }
  };

  const annuler = async (id) => {
    try { await api.delete('/leaves/' + id); load(); } catch (e) {}
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <CalendarDays className="w-7 h-7 text-amber-600" />
        <h1 className="text-2xl font-bold text-gray-800">Mes Congés</h1>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
        <h2 className="font-semibold text-gray-700 mb-4">Nouvelle demande</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type de congé</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none">
              {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" required value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au (inclus)</label>
            <input type="date" required value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">Motif (facultatif)</label>
          <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
            placeholder="Précisez si nécessaire…" />
        </div>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {ok && <p className="text-sm text-green-600 mb-3">{ok}</p>}
        <button type="submit" disabled={loading}
          className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50">
          {loading ? 'Envoi…' : 'Envoyer la demande'}
        </button>
      </form>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 font-semibold text-gray-700">Historique de mes demandes</div>
        {leaves.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 text-center">Aucune demande pour le moment.</p>
        ) : leaves.map(l => {
          const b = BADGES[l.status] || BADGES.pending;
          return (
            <div key={l.id} className="px-5 py-3 border-b border-gray-50 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium text-gray-800">{TYPES[l.type] || l.type} — {l.days} jour{l.days > 1 ? 's' : ''}</p>
                <p className="text-xs text-gray-500">Du {l.startDate} au {l.endDate}{l.reason ? ' · ' + l.reason : ''}</p>
                {l.status === 'rejected' && l.rejectionReason && (
                  <p className="text-xs text-red-500 mt-0.5">Motif du refus : {l.rejectionReason}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${b.cls}`}>
                  <b.Icon className="w-3.5 h-3.5" /> {b.label}
                </span>
                {l.status === 'pending' && (
                  <button onClick={() => annuler(l.id)} title="Annuler la demande"
                    className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
